// Temporary diagnostics: no raw records, SQL, tags, content, or arbitrary error text.
export const SURGEZIRC_SA_DIAGNOSTIC_ID = "7722bfa8-826d-4314-b17a-fd7c064fbd4f";
const templates = new Set(["magazine", "Magazine", "magazine@1.0.0"]);
const keys = new Set("id publicationId categoryId parentId sectionKey type enabled props data articles categories sections categorySections publishedArticles resolvedBlocks hero listing mostRead all items category raw title subtitle slug content body image imageAlt imageTitle imageCredit tags subCategory status deletedAt featured views publishedAt author authors articleAuthors position name username homepage navigation general organization settings limit columns featuredArticleId primaryTopic sql values".split(" "));
const sectionTypes = new Set(["hero", "category_feed", "article_grid", "article_list", "vertical_feed", "latest_news", "trending", "roundup", "ad_slot"]);
const cap = 200;
const read = (value, key) => {
  try { return value == null ? undefined : Object.getOwnPropertyDescriptor(Object(value), key)?.value; }
  catch { return undefined; }
};
const id = value => typeof value === "string" && /^[a-zA-Z0-9_-]{1,80}$/.test(value) ? value : undefined;
function shape(value) {
  try {
    const array = Array.isArray(value);
    const result = { typeof: typeof value, array, null: value === null };
    if (array || typeof value === "string") result.length = read(value, "length");
    else if (value !== null && typeof value === "object") {
      const names = Object.keys(value);
      result.keys = names.filter(key => keys.has(key));
      result.otherKeyCount = names.filter(key => !keys.has(key)).length;
    }
    return result;
  } catch { return { inspection: "unavailable" }; }
}
const identifier = value => id(value) ?? shape(value);
function recordShape(value) {
  return { ...shape(value), id: identifier(read(value, "id")), publicationId: identifier(read(value, "publicationId")),
    categoryId: identifier(read(value, "categoryId")), parentId: identifier(read(value, "parentId")), sectionKey: identifier(read(value, "sectionKey")) };
}
function collection(value, describe = recordShape) {
  const result = shape(value);
  if (result.array) {
    const length = result.length;
    if (Number.isSafeInteger(length) && length >= 0) {
      result.entries = [];
      for (let index = 0; index < Math.min(length, cap); index++) result.entries.push({ index, ...describe(read(value, String(index))) });
      result.omitted = Math.max(0, length - cap);
    }
  }
  return result;
}
function article(value) {
  return { ...recordShape(value), tags: shape(read(value, "tags")), subCategory: shape(read(value, "subCategory")),
    category: shape(read(value, "category")), authors: shape(read(value, "authors")), articleAuthors: shape(read(value, "articleAuthors")) };
}
function section(value) {
  const props = read(value, "props");
  const type = read(value, "type");
  const enabled = read(value, "enabled");
  const selected = {};
  for (const key of ["limit", "columns"]) {
    const item = read(props, key);
    selected[key] = { ...shape(item), ...(typeof item === "number" && Number.isFinite(item) ? { value: item } : {}) };
  }
  return { ...recordShape(value), type: sectionTypes.has(type) ? type : shape(type),
    enabled: typeof enabled === "boolean" ? enabled : shape(enabled),
    props: { ...shape(props), categoryId: identifier(read(props, "categoryId")), featuredArticleId: identifier(read(props, "featuredArticleId")), ...selected },
    category: recordShape(read(value, "category")),
    articles: collection(read(value, "articles"), article),
    data: { ...shape(read(value, "data")), articles: collection(read(read(value, "data"), "articles"), article) } };
}
function summary(value, kind = "shape") {
  try {
    if (kind === "publication") return { ...shape(value), publicationId: identifier(read(value, "id")) };
    if (kind === "categories") return collection(value);
    if (kind === "articles") return collection(value, article);
    if (kind === "article") return article(value);
    if (kind === "sections" || kind === "blocks") return collection(value, section);
    if (kind === "parameters") return collection(value, shape);
    if (kind === "settings") return { ...shape(value), publicationId: identifier(read(value, "publicationId")),
      homepage: { ...shape(read(value, "homepage")), sections: collection(read(read(value, "homepage"), "sections"), section),
        featuredArticleId: identifier(read(read(value, "homepage"), "featuredArticleId")) } };
    if (kind === "home") return { ...shape(value), publishedArticles: collection(read(value, "publishedArticles"), article), resolvedBlocks: collection(read(value, "resolvedBlocks"), section) };
    if (kind === "view") return { ...shape(value), categorySections: collection(read(value, "categorySections"), section),
      ...Object.fromEntries(["hero", "listing", "mostRead", "all"].map(key => [key, collection(read(value, key), article)])) };
    return shape(value);
  } catch { return { inspection: "unavailable" }; }
}
function errorSummary(error) {
  const message = read(error, "message");
  const code = read(error, "code");
  const databaseCode = read(read(error, "meta"), "code");
  const digest = read(error, "digest");
  const name = read(error, "name") || (error instanceof TypeError ? "TypeError" : error instanceof Error ? "Error" : "unknown");
  return {
    typeof: typeof error,
    name: /^(?:Error|TypeError|RangeError|SyntaxError|PrismaClient[A-Za-z]+Error)$/.test(name) ? name : "unknown",
    message: typeof message === "string" && /^Cannot read properties of (?:undefined|null) \(reading '(?:length|map|filter|id|articles|categoryId)'\)$/.test(message)
      ? message : "Message omitted: may contain query or private data",
    code: typeof code === "string" && /^(?:P\d{4}|E[A-Z_]{1,40})$/.test(code) ? code : undefined,
    databaseCode: typeof databaseCode === "string" && /^\d{3,5}$/.test(databaseCode) ? databaseCode : undefined,
    digest: typeof digest === "string" && /^\d{1,20}$/.test(digest) ? digest : undefined,
  };
}
export const silentMagazineDiagnostics = Object.freeze({ record() {}, observe(_stage, run) { return run(); } });

export function createMagazineDiagnostics(publication, requestId, { enabled = false, write = line => console.info(line), now = () => new Date().toISOString() } = {}) {
  if (!enabled || read(publication, "id") !== SURGEZIRC_SA_DIAGNOSTIC_ID || !templates.has(read(publication, "templateId"))) return silentMagazineDiagnostics;
  let sequence = 0;
  const emit = (stage, state, describe) => {
    try {
      write(JSON.stringify({ marker: "MAGAZINE_SA_DIAGNOSTIC", investigationDigest: "957918383", requestId: id(requestId),
        timestamp: now(), publicationId: SURGEZIRC_SA_DIAGNOSTIC_ID, sequence: ++sequence, stage, state, ...describe() }));
    } catch { /* Diagnostics must never change the request result or its exception. */ }
  };
  const record = (stage, values, kinds = {}) => emit(stage, "snapshot", () => ({ values: Object.fromEntries(
    Object.entries(typeof values === "function" ? values() : values).map(([key, value]) => [key, summary(value, kinds[key] || key)])) }));
  return {
    record,
    observe(stage, run, kind = "shape") {
      emit(stage, "start", () => ({}));
      const success = value => { emit(stage, "success", () => ({ result: summary(value, kind) })); return value; };
      const failure = error => { emit(stage, "error", () => ({ error: errorSummary(error) })); throw error; };
      try {
        const value = run();
        return value != null && typeof value.then === "function" ? Promise.resolve(value).then(success, failure) : success(value);
      } catch (error) { return failure(error); }
    },
  };
}
